import React, { useEffect, useState } from "react";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ENTITY_LABELS, ENTITY_ORDER } from "@/lib/entityColors";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Learner proposes a brand-new entity (thinker, text, concept…) to curators.
export const SuggestEntityDialog = ({ open, onOpenChange, presetName = "", presetText = "", presetUrl = "", presetTrack = null }) => {
  const [type, setType] = useState("concept");
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setName(presetName || ""); setText(presetText || ""); setUrl(presetUrl || ""); setType("concept"); }
  }, [open, presetName, presetText, presetUrl]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Give the entity a name"); return; }
    setBusy(true);
    try {
      await api.post("/contributions", {
        kind: "new_entity",
        proposed_type: type,
        proposed_name: name.trim(),
        text: text.trim(),
        url: url.trim(),
        track_id: presetTrack?.id || null,
      });
      toast.success("Sent to curators — thank you for enriching the graph");
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send. Please sign in."); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="suggest-entity-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" /> Suggest a new entity
          </DialogTitle>
          <DialogDescription>
            Propose a thinker, text, concept or school for curators to add to the source material.
            {presetTrack && <> Context: <b className="text-foreground">{presetTrack.label}</b>.</>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="mt-1" data-testid="suggest-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ENTITY_ORDER.map((t) => <SelectItem key={t} value={t}>{ENTITY_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Abhinavagupta" className="mt-1" data-testid="suggest-name" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Why it belongs / a short description</Label>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="What is it, and how does it connect to what's already here?" className="mt-1 min-h-[90px]" data-testid="suggest-text" />
          </div>
          <div>
            <Label className="text-xs">Source link (optional)</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" className="mt-1" data-testid="suggest-url" />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={submit} disabled={busy || !name.trim()} className="bg-primary hover:bg-primary/90" data-testid="suggest-submit">
            <Send className="w-4 h-4 mr-1.5" /> Send to curators
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
